import { useParams, useSearchParams } from 'react-router-dom';
import {
  APPROVAL_ID_STORAGE_KEY,
  POLICIES_APPROVALS_ROUTES,
  POLICY_ID_STORAGE_KEY,
} from './catalog.js';
import type { ApprovalRequest, Policy } from './policiesApprovalsApi.js';

export function policyListPath(): string {
  return POLICIES_APPROVALS_ROUTES['SCR-BIZ-013'];
}

export function policyEditorPath(policyId?: string): string {
  return policyId
    ? `${POLICIES_APPROVALS_ROUTES['SCR-BIZ-014']}/${encodeURIComponent(policyId)}`
    : POLICIES_APPROVALS_ROUTES['SCR-BIZ-014'];
}

export function policySimulatorPath(policyId?: string): string {
  return policyId
    ? `${POLICIES_APPROVALS_ROUTES['SCR-BIZ-015']}/${encodeURIComponent(policyId)}`
    : POLICIES_APPROVALS_ROUTES['SCR-BIZ-015'];
}

export function approvalChainPath(opts?: { delegation?: boolean }): string {
  const base = POLICIES_APPROVALS_ROUTES['SCR-BIZ-016'];
  return opts?.delegation ? `${base}?delegation=1` : base;
}

export function delegationSettingsPath(): string {
  return POLICIES_APPROVALS_ROUTES['SCR-BIZ-017'];
}

export function isDelegationOverlayPath(pathname: string, search = ''): boolean {
  if (pathname === POLICIES_APPROVALS_ROUTES['SCR-BIZ-017']) return true;
  if (pathname.startsWith(`${POLICIES_APPROVALS_ROUTES['SCR-BIZ-017']}/`)) return true;
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  return (
    pathname === POLICIES_APPROVALS_ROUTES['SCR-BIZ-016'] &&
    (params.get('delegation') === '1' || params.get('delegate') === '1')
  );
}

export function closeDelegationPath(): string {
  return POLICIES_APPROVALS_ROUTES['SCR-BIZ-016'];
}

export function approvalQueuePath(): string {
  return POLICIES_APPROVALS_ROUTES['SCR-BIZ-018'];
}

/** Canonical 07-03 path. Legacy `/approval-detail?approvalId=` remains as a fallback. */
export function approvalDetailPath(approvalId?: string): string {
  return approvalId ? `/approvals/${encodeURIComponent(approvalId)}` : '/approvals';
}

export function legacyApprovalDetailPath(approvalId?: string): string {
  return approvalId
    ? `${POLICIES_APPROVALS_ROUTES['SCR-BIZ-019']}?approvalId=${encodeURIComponent(approvalId)}`
    : POLICIES_APPROVALS_ROUTES['SCR-BIZ-019'];
}

function firstParam(...values: Array<string | null | undefined>): string | null {
  for (const value of values) {
    const trimmed = value?.trim();
    if (trimmed) return trimmed;
  }
  return null;
}

export function usePolicyIdParam(options?: { allowStored?: boolean }): string | null {
  const [params] = useSearchParams();
  const path = useParams();
  const fromUrl = firstParam(params.get('policyId'), path.policyId, path.id);
  if (fromUrl) return fromUrl;
  if (options?.allowStored === false) return null;
  return getStoredPolicyId();
}

export function useApprovalIdParam(options?: { allowStored?: boolean }): string | null {
  const [params] = useSearchParams();
  const path = useParams();
  const fromUrl = firstParam(params.get('approvalId'), path.id);
  if (fromUrl) return fromUrl;
  if (options?.allowStored === false) return null;
  return getStoredApprovalId();
}

export function getStoredPolicyId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(POLICY_ID_STORAGE_KEY);
}

export function setStoredPolicyId(policyId: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(POLICY_ID_STORAGE_KEY, policyId);
}

export function getStoredApprovalId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(APPROVAL_ID_STORAGE_KEY);
}

export function setStoredApprovalId(approvalId: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(APPROVAL_ID_STORAGE_KEY, approvalId);
}

export function policyLabel(policy: Policy): string {
  return policy.name ?? policy.id ?? 'Policy';
}

export function approvalLabel(approval: ApprovalRequest): string {
  return (
    approval.requesterName ??
    approval.summary ??
    approval.justification ??
    approval.reference ??
    approval.id ??
    'Approval request'
  );
}

export function assignmentCountLabel(count: number | null | undefined): string {
  if (count == null) return 'Assignments unknown';
  if (count === 0) return 'Not assigned yet';
  if (count === 1) return '1 person';
  return `${count} people`;
}
